import { TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { User2 } from "lucide-react";
import { MultiSelect } from "@/components/ui/multi-select";
import type {
	OrganizationMemberDTO,
	GruppeDTO,
} from "@/lib/orgaAdmin/orgaAdmin.types";
import { OrgaMemberRole } from "@/lib/auth/auth.types";

interface UserTableRowProps {
	member: OrganizationMemberDTO;
	isSelected: boolean;
	currentGroupIds: string[];
	availableGroups: GruppeDTO[];
	organizationId: string;
	isCurrentUser?: boolean;
	onSelectToggle: (userId: string) => void;
	onGroupsChange: (userId: string, groupIds: string[]) => void;
	onRolesChange: (userId: string, roles: OrgaMemberRole[]) => void;
}

/**
 * UserTableRow component
 * @param member - The member
 * @param isSelected - Whether the member is selected
 * @param currentGroupIds - The current group ids
 * @param availableGroups - The available groups
 * @param onSelectToggle - The function to toggle the selection
 * @param onGroupsChange - The function to change the groups
 */
export const UserTableRow: React.FC<UserTableRowProps> = ({
	member,
	isSelected,
	currentGroupIds,
	availableGroups,
	organizationId,
	isCurrentUser = false,
	onSelectToggle,
	onGroupsChange,
	onRolesChange,
}) => {
	const invitation = (member.invitations ?? []).find(
		(inv) => inv.organizationId === organizationId,
	);
	const isAccepted = !invitation || !!invitation.acceptedAt;
	return (
		<TableRow className="hover:bg-gray-50 transition-colors">
			<TableCell className="pl-6">
				<input
					type="checkbox"
					className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
					checked={isSelected}
					onChange={() => onSelectToggle(member.id)}
				/>
			</TableCell>
			<TableCell>
				<div className="flex items-center gap-3">
					<div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
						<User2 className="h-4 w-4 text-gray-500" />
					</div>
					<div>
						<div className="font-medium text-gray-900">
							{member.name?.trim() ? member.name : "Unbenannt"}
						</div>
						<div className="text-sm text-gray-500">{member.email}</div>
					</div>
				</div>
			</TableCell>
			<TableCell>
				{isAccepted ? (
					<Badge variant="secondary" className="bg-green-100 text-green-800">
						Akzeptiert
					</Badge>
				) : (
					<Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
						Eingeladen
					</Badge>
				)}
			</TableCell>
			<TableCell>
				<div className="w-48">
					{isCurrentUser ? (
						<div className="flex flex-wrap gap-1">
							{member.orgaRoles.length === 0 ? (
								<span className="text-gray-400 text-sm italic">
									Keine Rollen
								</span>
							) : (
								member.orgaRoles.map((role) => (
									<Badge
										key={role}
										variant="secondary"
										className="bg-blue-100 text-blue-700"
									>
										{role}
									</Badge>
								))
							)}
						</div>
					) : (
						<MultiSelect
							options={Object.values(OrgaMemberRole).map((role) => ({
								value: role,
								label: role,
							}))}
							values={member.orgaRoles.map((role) => role as string)}
							onChange={(next) =>
								onRolesChange(member.id, next as OrgaMemberRole[])
							}
							placeholder="Rollen auswählen..."
							maxVisibleItems={2}
						/>
					)}
				</div>
			</TableCell>
			<TableCell>
				<div className="flex flex-wrap gap-1">
					{(member.gruppen ?? []).length === 0 ? (
						<span className="text-gray-400 text-sm italic">Keine Gruppen</span>
					) : (
						(member.gruppen ?? []).map((g) => (
							<Badge
								key={g.id}
								variant="secondary"
								className="bg-gray-100 text-gray-700 hover:bg-gray-200"
							>
								{g.title}
							</Badge>
						))
					)}
				</div>
			</TableCell>
			<TableCell>
				<div className="w-64">
					<MultiSelect
						options={availableGroups.map((g) => ({
							value: g.id,
							label: g.title,
						}))}
						values={currentGroupIds}
						onChange={(next) => onGroupsChange(member.id, next)}
						placeholder="Gruppen auswählen..."
						maxVisibleItems={2}
					/>
				</div>
			</TableCell>
		</TableRow>
	);
};

export default UserTableRow;
